import { useQueryClient } from '@tanstack/react-query';
import Trans from 'next-translate/Trans';
import useTranslation from 'next-translate/useTranslation';
import { memo, useEffect } from 'react';

import { FAQService, getParamsQueriesKeys } from '@core/api';
import {
  Box,
  EditorContent,
  Icon,
  Line,
  Link,
  LinkTranslatedStyledAsLink,
  Pagination,
  Typography,
} from '@core/component';
import { FAQ_SEARCH_PER_PAGE, Translate } from '@core/constant';
import { useContextMediaQuery, useContextRouting, useContextUtil } from '@core/context';
import { useLanguage, useMutationApi } from '@core/hook';
import {
  EActionAttribute,
  EColor,
  EFontWeight,
  EIcon,
  EPosition,
  ERouting,
  ESize,
  ETypographyVariant,
} from '@core/type';
import { EQueryKey, FAQSearchEntity } from '@core/type/api';
import { replaceToBold } from '@core/util';

import { PageFaqSearchProps } from '../interface-faq';

const SearchResultBlockBase = ({ itemsCount, faqArticles: articles }: PageFaqSearchProps) => {
  const queryClient = useQueryClient();
  const { query, setQuery } = useContextRouting();
  const { isPhone } = useContextMediaQuery();
  const { axiosInstance } = useContextUtil();
  const { language } = useLanguage();

  const { t: tFAQ } = useTranslation(Translate.page.FAQ);

  const { mutate: changePage } = useMutationApi(
    async (pageParam: number) =>
      FAQService.searchFAQArticles(
        axiosInstance,
        language,
        (query as { q: string })?.q,
        pageParam,
        FAQ_SEARCH_PER_PAGE,
      ),
    {
      onSuccess: (result: FAQSearchEntity, page: number) => {
        if (result) {
          const { queries } = getParamsQueriesKeys<{ page?: string; q?: string }, unknown>({
            queryKey: EQueryKey.FAQ_SEARCH,
            axiosInstance,
            language,
            params: {
              page: page.toString(),
              q: (query as { q: string })?.q,
            },
          });
          queryClient.setQueryData(queries, (): FAQSearchEntity => {
            return result;
          });
          setQuery(page === 1 ? { q: query?.q } : { q: query?.q, page: page.toString() });
        } else {
          setQuery({ q: query?.q });
        }
      },
    },
  );

  const { mutate: changeLanguage } = useMutationApi(
    async () =>
      FAQService.searchFAQArticles(
        axiosInstance,
        language,
        (query as { q: string })?.q,
        Number(query?.page) || 1,
        FAQ_SEARCH_PER_PAGE,
      ),
    {
      onSuccess: (result: FAQSearchEntity) => {
        const { queries } = getParamsQueriesKeys<{ page?: string; q?: string }, unknown>({
          queryKey: EQueryKey.FAQ_SEARCH,
          axiosInstance,
          language,
          params: {
            page: (query?.page as string) || '1',
            q: (query as { q: string })?.q,
          },
        });
        queryClient.setQueryData(queries, (): FAQSearchEntity => {
          return result;
        });
      },
    },
  );

  useEffect(() => {
    if (articles?.length <= 0) {
      changePage(1);
      setQuery({ q: query?.q });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [articles]);

  useEffect(() => {
    changeLanguage();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [language]);

  return (
    <Box marginTop={isPhone ? '48px' : '66px'} marginBottom={isPhone ? '104px' : '140px'}>
      <Typography marginBottom={isPhone ? '28px' : '44px'}>
        <Trans
          i18nKey={`${Translate.page.FAQ}:searchResult`}
          values={{ query: query?.q }}
          components={{
            boldText: <Typography as={EActionAttribute.SPAN} fontWeight={EFontWeight.BOLD} />,
          }}
        />
        <Typography as={EActionAttribute.SPAN} color={EColor.INFO}>
          {' '}
          <Trans
            i18nKey={`${Translate.page.FAQ}:${itemsCount ? 'oneArticle' : 'manyArticles'}`}
            values={{ count: itemsCount }}
          />
        </Typography>
      </Typography>

      {itemsCount === 0 ? (
        <Typography
          variant={ETypographyVariant.H5}
          fontWeight={EFontWeight.REGULAR}
          color={EColor.INFO}
          textAlign={'center'}
          maxWidth={'422px'}
          margin={isPhone ? '64px auto 0' : '88px auto 0'}
        >
          {tFAQ('emptyResult')}
        </Typography>
      ) : (
        <>
          {articles?.map((article, index) => (
            <>
              {index > 0 && <Line />}
              <Box padding={isPhone ? '0' : '20px 0'} key={article.slug}>
                <LinkTranslatedStyledAsLink
                  keyRoute={ERouting.FAQ_SHOW}
                  query={{ slug: article.slug }}
                >
                  <Box
                    display={'flex'}
                    alignItems={'center'}
                    justifyContent={'space-between'}
                    padding={'20px 0'}
                    cursor={'pointer'}
                  >
                    <Link variant={ETypographyVariant.H5} fontWeight={EFontWeight.REGULAR}>
                      <EditorContent content={replaceToBold(query?.q as string, article.name)} />
                    </Link>
                    <Icon type={EIcon.ANGLE} size={ESize.MD} orientation={EPosition.RIGHT} />
                  </Box>
                </LinkTranslatedStyledAsLink>
              </Box>
            </>
          ))}

          {itemsCount > FAQ_SEARCH_PER_PAGE && (
            <Box marginTop={isPhone ? '56px' : '80px'}>
              <Pagination
                page={Number(query?.page) || 1}
                perPage={FAQ_SEARCH_PER_PAGE}
                itemsCount={itemsCount}
                onPageChange={changePage}
              />
            </Box>
          )}
        </>
      )}
    </Box>
  );
};

export const SearchResultBlock = memo(SearchResultBlockBase);
